import React, { useEffect, useMemo, useState } from 'react';
import { Formik, Form as FormikForm } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import CloseIcon from '@mui/icons-material/Close';
import NInput from '../../components/inputs/NInput';
import NDropdown from '../../components/inputs/NDropdown';
import NButton from '../../components/inputs/NButton';
import { addCustomerValidation } from '../../validations/validation';
import useCustomerAPI from '../../hooks/network/useCustomerAPI';
import { errorToaster, successToaster } from '../../components/toaster/Toaster';
import { ResponseMessage } from '../../constants/Constants';
import { RouteEnums } from '../../Routes/RoutesConstant';
import { useFormHook } from '../../hooks/custom-hooks/useFormHook';
import useWidgetAPI from '../../hooks/network/useWidgetAPI';

const formInitialValues = {
  customerName: '',
  emailID: '',
  sapid: '',
  phoneNumber: '',
  address: '',
};

function AddCustomer() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { getCustomer, updateCustomer, addCustomer, isLoading } = useCustomerAPI();
  const { getDropDownOptions: getCustomerOptionAPI } = useWidgetAPI();
  const { formRef, formHeight } = useFormHook();

  const [formValues, setFormValues] = useState(formInitialValues);
  const [customerOptions, setCustomerOptions] = useState();

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (customerId) {
      fetchCustomerDetails();
    }
  }, [customerId]);

  const fetchCustomerDetails = async () => {
    try {
      const response = await getCustomer(customerId);
      setFormValues(response);
    } catch {}
  };

  const fetchCustomers = async () => {
    try {
      const response = await getCustomerOptionAPI();
      setCustomerOptions(response);
    } catch {}
  };

  const handleFormSubmit = async (values, formikBag) => {
    try {
      if (customerId) {
        const response = await updateCustomer(customerId, values);
        if (response?.success) {
          successToaster(response?.message || 'Customer Updated');
          navigate(RouteEnums.CUSTOMER);
        } else errorToaster(response?.message || response?.error || ResponseMessage.ERROR_DEFAULT);
      } else {
        const response = await addCustomer(values);
        if (response?.success) {
          formikBag.resetForm();
          setFormValues(formInitialValues);
          successToaster(response?.message || 'Customer Added');
          navigate(RouteEnums.CUSTOMER);
        } else errorToaster(response?.message || response?.error || ResponseMessage.ERROR_DEFAULT);
      }
    } catch {}
  };

  const getCustomerOptions = useMemo(
    () =>
      customerOptions?.map((o) => ({
        label: o.customerName,
        value: o.customerName,
      })),
    [customerOptions],
  );

  return (
    <>
      <div className='nmobile-form-blur' />
      <div
        style={{
          height:
            window.innerWidth < 1180
              ? `min(calc(100vh - 150px), calc(100vh - ${formHeight}px))`
              : '100%',
        }}
        className='nform-wrapper'
      >
        <div className='nform-container'>
          {/* Mobile Header Heading */}
          <div className='nmobile-title'>
            <h2 className='app-heading h2-style'>{customerId ? 'Edit' : 'Add'} Customer</h2>
            <div onClick={() => navigate(RouteEnums.CUSTOMER)}>
              <CloseIcon />
            </div>
          </div>

          <div className='nform'>
            <div ref={formRef}>
              <Formik
                initialValues={formValues}
                validationSchema={addCustomerValidation}
                onSubmit={(values, formikBag) => {
                  handleFormSubmit(values, formikBag);
                }}
                enableReinitialize
              >
                {({
                  touched,
                  values,
                  errors,
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                }) => (
                  <FormikForm className='' autoComplete='off' onSubmit={handleSubmit}>
                    {/* Desktop buttons */}
                    <div className='ntitle-nAction-button'>
                      <h2 className='app-heading h2-style'>
                        {customerId ? 'Edit' : 'Add'} Customer
                      </h2>
                      <div className='naction-btns'>
                        <div className={clsx('hidden', 'break:block')}>
                          <NButton variant='Noutline' onClick={() => navigate(RouteEnums.CUSTOMER)}>
                            Cancel
                          </NButton>
                        </div>
                        <div>
                          <NButton
                            isLoading={isLoading}
                            onClick={handleSubmit}
                            variant='Ncontained'
                          >
                            {customerId ? 'Update' : 'Save'} Customer
                          </NButton>
                        </div>
                      </div>
                    </div>

                    {/* Common form */}
                    <div className='nform-fields'>
                      <div className='w-full'>
                        {customerId ? (
                          <NInput
                            required
                            name='customerName'
                            value={values.customerName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.customerName && touched.customerName}
                            helperText={
                              errors.customerName && touched.customerName ? errors.customerName : ''
                            }
                            className='w-full'
                            label='Customer Name'
                            placeholder='Enter Your Customer Name'
                            type='text'
                          />
                        ) : (
                          <NDropdown
                            required
                            label='Customer Name*'
                            name='customerName'
                            value={values.customerName}
                            onChange={(e) => {
                              handleChange(e);
                              setFieldValue(
                                'sapid',
                                customerOptions?.find((c) => c.customerName === e?.target?.value)
                                  ?.customerID,
                              );
                            }}
                            onBlur={handleBlur}
                            error={errors.customerName && touched.customerName}
                            helperText={
                              errors.customerName && touched.customerName ? errors.customerName : ''
                            }
                            className='w-full'
                            type='text'
                            options={getCustomerOptions}
                          />
                        )}
                      </div>
                      <div className='w-full'>
                        <NInput
                          required
                          name='emailID'
                          value={values.emailID}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.emailID && touched.emailID}
                          helperText={errors.emailID && touched.emailID ? errors.emailID : ''}
                          className='w-full'
                          label='Email ID'
                          placeholder='Enter Your Email address'
                          type='text'
                        />
                      </div>
                      <div className='w-full'>
                        <NInput
                          required
                          name='sapid'
                          value={values.sapid}
                          onChange={(e) => handleChange(e)}
                          onBlur={handleBlur}
                          error={errors.sapid && touched.sapid}
                          helperText={errors.sapid && touched.sapid ? errors.sapid : ''}
                          className='w-full'
                          label='SAP ID'
                          placeholder='Enter Your SAP ID'
                          type='text'
                        />
                      </div>
                      <div className='w-full'>
                        <NInput
                          name='phoneNumber'
                          value={values.phoneNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.phoneNumber && touched.phoneNumber}
                          helperText={
                            errors.phoneNumber && touched.phoneNumber ? errors.phoneNumber : ''
                          }
                          className='w-full'
                          label='Phone Number'
                          placeholder='Enter Your Phone Number'
                          type='text'
                        />
                      </div>
                      <div className='w-full'>
                        <NInput
                          name='address'
                          value={values.address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.address && touched.address}
                          helperText={errors.address && touched.address ? errors.address : ''}
                          className='w-full'
                          label='Address'
                          placeholder='Enter Your address'
                          type='text'
                        />
                      </div>
                    </div>

                    {/* Mobile Buttons */}
                    <div className='nmobile-action-btn'>
                      <div>
                        <NButton isLoading={isLoading} onClick={handleSubmit} variant='Ncontained'>
                          {customerId ? 'Update' : 'Save'} Customer
                        </NButton>
                      </div>
                    </div>
                  </FormikForm>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddCustomer;
